import React, { useRef, useState } from 'react';
import moment from 'moment-timezone';
import Button from '@amzn/meridian/button';
import undoTokens from '@amzn/meridian-tokens/base/icon/undo';
import Row from '@amzn/meridian/row';
import Column from '@amzn/meridian/column';
import Table, { TableCell, TableRow } from '@amzn/meridian/table';
import Text from '@amzn/meridian/text';
import Link from '@amzn/meridian/link';
import Popover, { PopoverHeader } from '@amzn/meridian/popover';
import { SSD_OVERRIDE_REASON_CODES, BUSINESS_VIEW_NAME, CORE_BUSINESS_TYPE_LABEL } from '../../../../utils/constants';
import Select, { SelectOption } from '@amzn/meridian/select';
import Input from '@amzn/meridian/input';
import { chainWalk, convertEpochToDatetimeLocal } from '../../../../utils/helpers';
import Icon from '@amzn/meridian/icon';
import { displayTime, getShiftSchedules, getTimelineForViewPlan } from '../../utils';

const USER_OVERRIDE_REASON_CODES = [
  SSD_OVERRIDE_REASON_CODES.EXCESSIVE_ATTRITION,
  SSD_OVERRIDE_REASON_CODES.PLANNED_EVENT
];

const OverrideHeadcountPopover = ({ currentUser, plan, setPlan, headcount }) => {
  const linkRef = useRef();
  const [open, setOpen] = useState(false);
  const { scheduledHeadcount, onsiteHeadcount, availableHeadcount, expectedAttendanceHeadcount } = headcount || {};

  const overridingValue = chainWalk(() => headcount.overrides.overrideKeyValueMap.availableHeadcount, null);
  const reasonCode = chainWalk(() => headcount.overrides.reason, null);
  const minHeadcount = chainWalk(() => headcount.overrides.overrideKeyGuardrailsMap.availableHeadcount.minimum, 0);
  const maxHeadcount = chainWalk(
    () => headcount.overrides.overrideKeyGuardrailsMap.availableHeadcount.maximum,
    Number.POSITIVE_INFINITY
  );

  const initialValue = overridingValue != null ? overridingValue : availableHeadcount;
  const [newOverridingValue, setNewOverridingValue] = useState(initialValue);
  const [selectedOverrideReason, setSelectedOverrideReason] = useState(reasonCode);
  const attendancePercentage =
    scheduledHeadcount != null ? Math.round((newOverridingValue / scheduledHeadcount) * 100) : '';

  const onSubmit = () => {
    if (!headcount.overrides) {
      headcount.overrides = {};
    }
    if (!headcount.overrides.overrideKeyValueMap) {
      headcount.overrides.overrideKeyValueMap = {};
    }
    headcount.overrides.overrideKeyValueMap.availableHeadcount = newOverridingValue + '';
    headcount.overrides.reason = selectedOverrideReason;
    headcount.overrides.timestamp = parseInt(Date.now() / 1000);
    headcount.overrides.user = currentUser;
    setPlan({ ...plan });
    setOpen(false);
  };
  const onRevert = () => {
    if (headcount.overrides) {
      delete headcount.overrides.overrideKeyValueMap;
      delete headcount.overrides.users;
      delete headcount.overrides.reason;
    }
    setNewOverridingValue(availableHeadcount);
    setSelectedOverrideReason(null);
    setPlan({ ...plan });
  };

  const isValid = minHeadcount <= newOverridingValue && newOverridingValue <= maxHeadcount;
  const isDisabled = initialValue === newOverridingValue || !isValid || !selectedOverrideReason;
  const isRevertible = overridingValue !== null;

  return (
    <React.Fragment>
      {isRevertible && (
        <Button type={'link'} onClick={onRevert}>
          <Icon tokens={undoTokens} />
        </Button>
      )}
      <Link type="secondary" onClick={() => setOpen(true)} ref={linkRef}>
        {overridingValue != null ? overridingValue : availableHeadcount}
      </Link>
      <Popover anchorNode={linkRef.current} open={open} onClose={() => setOpen(false)} position="bottom">
        <PopoverHeader closeLabel="Close">Provide an override</PopoverHeader>
        <Column>
          <Row width="100%" alignmentHorizontal="justify">
            <Text>Scheduled HC</Text>
            <Text>{scheduledHeadcount}</Text>
          </Row>
          <Row width="100%" alignmentHorizontal="justify">
            <Text>Onsite HC</Text>
            <Text>{onsiteHeadcount}</Text>
          </Row>
          <Row width="100%" alignmentHorizontal="justify">
            <Text>Expected Attendance HC</Text>
            <Text alignment="end">{expectedAttendanceHeadcount}</Text>
          </Row>
          <Row width="100%" alignmentHorizontal="justify">
            <Text>Available HC</Text>
            <Input
              width={200}
              value={newOverridingValue}
              onChange={setNewOverridingValue}
              error={!isValid}
              type="number"
              size="small"
              errorMessage={!isValid ? `Headcount value must be between ${minHeadcount} and ${maxHeadcount}` : ''}
            />
          </Row>
          <Row width="100%" widths="fill">
            <Text>Shift Half Attendance Rate</Text>
            <Text alignment="end">{attendancePercentage}%</Text>
          </Row>
          <Select
            value={selectedOverrideReason}
            onChange={setSelectedOverrideReason}
            placeholder="Select a reason"
            size="small">
            {USER_OVERRIDE_REASON_CODES.map(({ value, label }) => (
              <SelectOption key={value} value={value} label={label} />
            ))}
          </Select>
          <Button disabled={isDisabled} onClick={onSubmit}>
            Submit Override
          </Button>
        </Column>
      </Popover>
    </React.Fragment>
  );
};

const getAvailableHeadcount = (headcount) => {
  if (!headcount) return 0;
  const existingOverrides = chainWalk(() => headcount.overrides.overrideKeyValueMap.availableHeadcount, null);
  const availableHeadcount = headcount.availableHeadcount ?? 0;
  const result = existingOverrides != null ? Number(existingOverrides) : Number(availableHeadcount);
  return Number.isFinite(result) ? result : 0;
};

const createOverrideCell = (currentUser, plan, setPlan) => (headcount) =>
  headcount ? (
    <OverrideHeadcountPopover currentUser={currentUser} plan={plan} setPlan={setPlan} headcount={headcount} />
  ) : (
    <>-</>
  );

const parseAndSortHeadcountsByTime = (plan) => {
  const headcountList = chainWalk(() => plan.input.ssdShiftPlanInput.headcountList, []);
  return [...headcountList].sort((a, b) => (a.startTime || 0) - (b.startTime || 0));
};

const formatTimeRange = (startTime, endTime, timezone) => {
  const startMoment = moment.unix(startTime).tz(timezone);
  const date = startMoment.format('MM/DD');
  const startTime24h = startMoment.format('HH:mm');
  const endTime24h = moment.unix(endTime).tz(timezone).format('HH:mm');
  return `${date} (${startTime24h}-${endTime24h})`;
};

const renderSectionHeader = (label, columnSpan) => (
  <TableRow backgroundColor="secondary">
    <TableCell>
      <Text type="h100">{label}</Text>
    </TableCell>
    <TableCell columnSpan={columnSpan}></TableCell>
  </TableRow>
);

export const HeadcountShiftSingleBusiness = ({ currentUser, plan, setPlan }) => {
  const timeline = getTimelineForViewPlan(plan);
  const headcounts = chainWalk(() => plan.input.ssdShiftPlanInput.headcountList, []);
  const shiftSchedules = getShiftSchedules(timeline);
  const headcountRows = [];
  shiftSchedules.forEach((shiftSchedule) => {
    const firstHalfHeadcount = chainWalk(
      () =>
        headcounts.find((headcount) => headcount.shiftHalfStartTimestamp === shiftSchedule.firstHalf.startTimestamp),
      {}
    );
    const secondHalfHeadcount = chainWalk(
      () =>
        headcounts.find((headcount) => headcount.shiftHalfStartTimestamp === shiftSchedule.secondHalf.startTimestamp),
      {}
    );
    headcountRows.push(
      <TableRow key={shiftSchedule.label}>
        <TableCell>
          <Text type="h100">{shiftSchedule.label}</Text>
        </TableCell>
        <TableCell>{firstHalfHeadcount.scheduledHeadcount}</TableCell>
        <TableCell>
          <OverrideHeadcountPopover
            currentUser={currentUser}
            plan={plan}
            setPlan={setPlan}
            headcount={firstHalfHeadcount}
          />
        </TableCell>
        <TableCell>{secondHalfHeadcount.scheduledHeadcount}</TableCell>
        <TableCell>
          <OverrideHeadcountPopover
            currentUser={currentUser}
            plan={plan}
            setPlan={setPlan}
            headcount={secondHalfHeadcount}
          />
        </TableCell>
      </TableRow>
    );
  });

  return (
    <div style={{ overflowX: 'auto', maxWidth: '100%' }}>
      <Table headerRows={1} showDividers={true}>
        <TableRow>
          <TableCell>Window</TableCell>
          <TableCell>1H Scheduled</TableCell>
          <TableCell>1H Available</TableCell>
          <TableCell>2H Scheduled</TableCell>
          <TableCell>2H Available</TableCell>
        </TableRow>
        {renderSectionHeader('Active Shift', 4)}
        {headcountRows[0]}
        {renderSectionHeader('Upcoming Shifts', 4)}
        {headcountRows.slice(1)}
      </Table>
    </div>
  );
};

export const HeadcountShiftMultiBusiness = ({ currentUser, plan, setPlan }) => {
  const timeline = getTimelineForViewPlan(plan);
  const headcounts = chainWalk(() => plan.input.ssdShiftPlanInput.headcountList, []);
  const shiftSchedules = getShiftSchedules(timeline);

  const findHeadcountByBusiness = (startTimestamp, business) => {
    if (startTimestamp == null) return null;
    return (
      headcounts.find((headcount) => {
        if (headcount.shiftHalfStartTimestamp !== startTimestamp) return false;
        return headcount.businessType === business;
      }) || null
    );
  };

  const overrideCell = createOverrideCell(currentUser, plan, setPlan);

  const buildWindowRows = (label, firstHalfStart, secondHalfStart) => {
    const coreFirstHalf = findHeadcountByBusiness(firstHalfStart, BUSINESS_VIEW_NAME.CORE);
    const coreSecondHalf = findHeadcountByBusiness(secondHalfStart, BUSINESS_VIEW_NAME.CORE);
    const groceryFirstHalf = findHeadcountByBusiness(firstHalfStart, BUSINESS_VIEW_NAME.GROCERY);
    const grocerySecondHalf = findHeadcountByBusiness(secondHalfStart, BUSINESS_VIEW_NAME.GROCERY);

    const core = {
      scheduled1H: Number(coreFirstHalf?.scheduledHeadcount ?? 0),
      available1H: getAvailableHeadcount(coreFirstHalf),
      scheduled2H: Number(coreSecondHalf?.scheduledHeadcount ?? 0),
      available2H: getAvailableHeadcount(coreSecondHalf)
    };
    const grocery = {
      scheduled1H: Number(groceryFirstHalf?.scheduledHeadcount ?? 0),
      available1H: getAvailableHeadcount(groceryFirstHalf),
      scheduled2H: Number(grocerySecondHalf?.scheduledHeadcount ?? 0),
      available2H: getAvailableHeadcount(grocerySecondHalf)
    };
    const total = {
      scheduled1H: core.scheduled1H + grocery.scheduled1H,
      available1H: core.available1H + grocery.available1H,
      scheduled2H: core.scheduled2H + grocery.scheduled2H,
      available2H: core.available2H + grocery.available2H
    };

    const rows = [];

    rows.push(
      <TableRow key={`${label}-${BUSINESS_VIEW_NAME.CORE}`}>
        <TableCell>{label}</TableCell>
        <TableCell>{CORE_BUSINESS_TYPE_LABEL}</TableCell>
        <TableCell>{core.scheduled1H}</TableCell>
        <TableCell>{overrideCell(coreFirstHalf)}</TableCell>
        <TableCell>{core.scheduled2H}</TableCell>
        <TableCell>{overrideCell(coreSecondHalf)}</TableCell>
      </TableRow>
    );

    rows.push(
      <TableRow key={`${label}-${BUSINESS_VIEW_NAME.GROCERY}`}>
        <TableCell />
        <TableCell>Grocery</TableCell>
        <TableCell>{grocery.scheduled1H}</TableCell>
        <TableCell>{overrideCell(groceryFirstHalf)}</TableCell>
        <TableCell>{grocery.scheduled2H}</TableCell>
        <TableCell>{overrideCell(grocerySecondHalf)}</TableCell>
      </TableRow>
    );

    rows.push(
      <TableRow key={`${label}-total`} backgroundColor="secondary">
        <TableCell>Window Total</TableCell>
        <TableCell />
        <TableCell>{total.scheduled1H}</TableCell>
        <TableCell>{total.available1H}</TableCell>
        <TableCell>{total.scheduled2H}</TableCell>
        <TableCell>{total.available2H}</TableCell>
      </TableRow>
    );

    return { rows, total };
  };

  const windowBlocks = shiftSchedules.map((shiftSchedule) =>
    buildWindowRows(
      shiftSchedule.label,
      chainWalk(() => shiftSchedule.firstHalf.startTimestamp, null),
      chainWalk(() => shiftSchedule.secondHalf.startTimestamp, null)
    )
  );

  const first = windowBlocks[0];
  const rest = windowBlocks.slice(1);
  const rows = [];

  rows.push(
    <TableRow key="headerRow">
      <TableCell>Window</TableCell>
      <TableCell>Business</TableCell>
      <TableCell>1H Scheduled</TableCell>
      <TableCell>1H Available</TableCell>
      <TableCell>2H Scheduled</TableCell>
      <TableCell>2H Available</TableCell>
    </TableRow>
  );

  if (first) {
    rows.push(renderSectionHeader('Active Shift', 5));
    rows.push(...first.rows);
  }

  if (rest.length > 0) {
    rows.push(renderSectionHeader('Upcoming Shifts', 5));
    rest.forEach((b, i) => rows.push(...b.rows));
  }

  return (
    <div style={{ overflowX: 'auto', maxWidth: '100%' }}>
      <Table headerRows={1} showDividers={true}>
        {rows}
      </Table>
    </div>
  );
};

export const HeadcountHourlySingleBusiness = ({ currentUser, plan, setPlan }) => {
  const timezone = chainWalk(() => JSON.parse(plan.input.adminSettings).timezone, 'UTC');
  const sortedHeadcounts = parseAndSortHeadcountsByTime(plan);
  const now = moment().tz(timezone).unix();
  const currentHourIndex = sortedHeadcounts.findIndex((h) => h.startTime <= now && now < h.endTime);

  return (
    <div style={{ overflowX: 'auto', maxWidth: '100%' }}>
      <Table headerRows={1} showDividers={true}>
        <TableRow>
          <TableCell>Hour</TableCell>
          <TableCell>Scheduled</TableCell>
          <TableCell>Available</TableCell>
        </TableRow>
        {currentHourIndex >= 0 && (
          <>
            {renderSectionHeader('Current Hour', 2)}
            <TableRow key={currentHourIndex}>
              <TableCell>
                <Text type="h100">
                  {formatTimeRange(
                    sortedHeadcounts[currentHourIndex].startTime,
                    sortedHeadcounts[currentHourIndex].endTime,
                    timezone
                  )}
                </Text>
              </TableCell>
              <TableCell>{sortedHeadcounts[currentHourIndex].scheduledHeadcount}</TableCell>
              <TableCell>
                <OverrideHeadcountPopover
                  currentUser={currentUser}
                  plan={plan}
                  setPlan={setPlan}
                  headcount={sortedHeadcounts[currentHourIndex]}
                />
              </TableCell>
            </TableRow>
          </>
        )}
        {currentHourIndex >= 0 &&
          currentHourIndex < sortedHeadcounts.length - 1 &&
          renderSectionHeader('Upcoming Hours', 2)}
        {sortedHeadcounts.map((headcount, index) => {
          if (index === currentHourIndex) return null;
          return (
            <TableRow key={index}>
              <TableCell>
                <Text type="h100">{formatTimeRange(headcount.startTime, headcount.endTime, timezone)}</Text>
              </TableCell>
              <TableCell>{headcount.scheduledHeadcount}</TableCell>
              <TableCell>
                <OverrideHeadcountPopover
                  currentUser={currentUser}
                  plan={plan}
                  setPlan={setPlan}
                  headcount={headcount}
                />
              </TableCell>
            </TableRow>
          );
        })}
      </Table>
    </div>
  );
};

export const HeadcountHourlyMultiBusiness = ({ currentUser, plan, setPlan }) => {
  const timezone = chainWalk(() => JSON.parse(plan.input.adminSettings).timezone, 'UTC');
  const sortedHeadcounts = parseAndSortHeadcountsByTime(plan);
  const overrideCell = createOverrideCell(currentUser, plan, setPlan);

  const windowsByTime = {};
  sortedHeadcounts.forEach((headcount) => {
    const key = `${headcount.startTime}-${headcount.endTime}`;
    if (!windowsByTime[key]) {
      windowsByTime[key] = { startTime: headcount.startTime, endTime: headcount.endTime, headcounts: [] };
    }
    windowsByTime[key].headcounts.push(headcount);
  });

  const windows = Object.values(windowsByTime);
  const now = moment().tz(timezone).unix();
  const currentWindowIndex = windows.findIndex((w) => w.startTime <= now && now < w.endTime);

  const renderWindowRows = (window, windowIndex) => {
    const coreHeadcount = window.headcounts.find((h) => h.businessType === BUSINESS_VIEW_NAME.CORE);
    const groceryHeadcount = window.headcounts.find((h) => h.businessType === BUSINESS_VIEW_NAME.GROCERY);
    const coreScheduled = Number(coreHeadcount?.scheduledHeadcount ?? 0);
    const coreAvailable = getAvailableHeadcount(coreHeadcount);
    const groceryScheduled = Number(groceryHeadcount?.scheduledHeadcount ?? 0);
    const groceryAvailable = getAvailableHeadcount(groceryHeadcount);
    const timeLabel = formatTimeRange(window.startTime, window.endTime, timezone);

    return [
      <TableRow key={`${windowIndex}-core`}>
        <TableCell>
          <Text type="h100">{timeLabel}</Text>
        </TableCell>
        <TableCell>{CORE_BUSINESS_TYPE_LABEL}</TableCell>
        <TableCell>{coreScheduled}</TableCell>
        <TableCell>{overrideCell(coreHeadcount)}</TableCell>
      </TableRow>,
      <TableRow key={`${windowIndex}-grocery`}>
        <TableCell />
        <TableCell>Grocery</TableCell>
        <TableCell>{groceryScheduled}</TableCell>
        <TableCell>{overrideCell(groceryHeadcount)}</TableCell>
      </TableRow>,
      <TableRow key={`${windowIndex}-total`} backgroundColor="secondary">
        <TableCell>Hour Total</TableCell>
        <TableCell />
        <TableCell>{coreScheduled + groceryScheduled}</TableCell>
        <TableCell>{coreAvailable + groceryAvailable}</TableCell>
      </TableRow>
    ];
  };

  const rows = [
    <TableRow key="headerRow">
      <TableCell>Hour</TableCell>
      <TableCell>Business</TableCell>
      <TableCell>Scheduled</TableCell>
      <TableCell>Available</TableCell>
    </TableRow>
  ];

  if (currentWindowIndex >= 0) {
    rows.push(renderSectionHeader('Current Hour', 3));
    rows.push(...renderWindowRows(windows[currentWindowIndex], currentWindowIndex));
  }

  if (currentWindowIndex >= 0 && currentWindowIndex < windows.length - 1) {
    rows.push(renderSectionHeader('Upcoming Hours', 3));
  }

  windows.forEach((window, windowIndex) => {
    if (windowIndex === currentWindowIndex) return;
    rows.push(...renderWindowRows(window, windowIndex));
  });

  return (
    <div style={{ overflowX: 'auto', maxWidth: '100%' }}>
      <Table headerRows={1} showDividers={true}>
        {rows}
      </Table>
    </div>
  );
};
